import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { refreshSportsCatalog } from '../../../../components/Util/sportsCatalog';
import { useFunzioni } from '../../../../components/Util/featureFlags';
import { usePageTitle } from '../../../../layouts/PageHeaderExtra';
import { useStatoNelTitolo } from '../../../../components/Util/StatoProdotto';
import { TraguardiTabella } from './traguardi';

/*
 * Scheda di una disciplina sportiva (la nuova si crea da una finestra della lista, solo con il nome). Prima stava in un popup
 * della lista, con le azioni nel menu a tre puntini di ogni riga: ora la riga apre questa
 * pagina con lo stato (selettore che salva subito); modifica del nome ed Elimina stanno nella barra in fondo.
 *
 * Due tab: «Dati» e «Traguardi» (quest'ultimo e' il contenuto di traguardi.js).
 *
 * Regole che la scheda deve rendere evidenti, perche' non sono ovvie:
 *  - il CODICE non si cambia piu' dopo il salvataggio: e' la chiave con cui la
 *    disciplina compare in tesserini, corsi, squadre e abbonamenti;
 *  - una disciplina nuova e' "piatta": niente livelli, niente ruoli, niente Arena,
 *    icona generica. Quelle cose vivono nel codice, sulle discipline storiche, e non si
 *    aggiungono da qui;
 *  - le discipline core non si disattivano e non si eliminano.
 */

export const LIST_URL = '/superadmin/anagrafiche/discipline';
export const schedaUrl = (code, tab) => `${LIST_URL}/${code}${tab ? `?tab=${tab}` : ''}`;

export const getToken = () => {
    const details = sessionStorage.getItem('userDetails');
    if (!details) return null;
    return JSON.parse(details).idToken;
};

/*
 * Il codice si ricava dal nome, sempre, con la stessa convenzione del resto del
 * sistema: niente accenti, tutto maiuscolo, ogni gruppo di caratteri non alfanumerici
 * diventa un underscore.
 *
 *   'Subacquea'      -> SUBACQUEA
 *   'Voga in piedi'  -> VOGA_IN_PIEDI
 *   "Sci d'acqua"    -> SCI_D_ACQUA
 *   'Beach volley'   -> BEACH_VOLLEY
 *
 * Non si digita e non si corregge: e' una chiave tecnica, e lasciarla scrivere a mano
 * significherebbe solo offrire l'occasione di sbagliarla una volta per sempre, visto che
 * dopo il salvataggio non si cambia piu'.
 */
export const codeFromLabel = (label) =>
    String(label ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40)
        .replace(/_+$/, ''); // il taglio a 40 puo' lasciare un underscore in coda

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,39}$/;

// Una disciplina come arriva da GET admin/sports, con i campi che mostrano lista e scheda.
export const mapSport = (sport) => ({
    code: sport.code,
    label: sport.label,
    active: sport.active,
    core: sport.core,
    tipo: sport.core ? 'Core' : sport.legacy ? 'Storica' : 'Aggiunta',
    stato: sport.active ? 'Attiva' : 'Disattivata',
    // Assenti finche' il backend non li manda: si mostra un trattino
    traguardi: sport.activeMoveGoalsCount,
    traguardiDisattivati: (sport.moveGoalsCount ?? 0) - (sport.activeMoveGoalsCount ?? 0),
});

/*
 * Crea una disciplina dal solo nome (finestra «Nuova disciplina» della lista). Il codice si ricava dal
 * nome e non si mostra: gli errori si spiegano sul NOME. Restituisce { code } oppure { error }.
 */
export const creaDisciplina = async (nome, catalog) => {
    const label = String(nome ?? '').trim();
    if (label.length < 2) return { error: 'Il nome della disciplina deve avere almeno due caratteri.' };
    const code = codeFromLabel(label);
    if (code.length === 0) return { error: 'Il nome deve contenere almeno una lettera o un numero.' };
    if (!/^[A-Z]/.test(code)) {
        return { error: 'Il nome deve iniziare con una lettera: prova a girarlo, per esempio «Basket 3x3» invece di «3x3 Basket».' };
    }
    if (code.length < 2 || !CODE_PATTERN.test(code)) return { error: `Il nome «${label}» non va bene: prova con un nome più semplice.` };
    const existing = (catalog ?? []).find((s) => s.code === code);
    if (existing) {
        return { error: `Esiste già la disciplina «${existing.label}»${existing.active ? '' : ', al momento non attiva'}: usa quella o scegli un nome diverso.` };
    }
    const token = getToken();
    if (!token) return { error: 'Sessione scaduta: rientra nel Manager.' };
    const result = await new MyApiClient().createAdminSport(token, { code, label });
    if (result && result.error) return { error: result.error };
    await refreshSportsCatalog();
    return { code };
};

const TABS = ['dati', 'traguardi'];

export const SchedaDisciplina = () => {
    const { sportsCatalogCreation: SPORT_CATALOG_CREATION_ENABLED } = useFunzioni();
    const { code: codeParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = codeParam === undefined;

    const tab = new URLSearchParams(location.search).get('tab');
    const activeTab = TABS.includes(tab) ? tab : 'dati';
    const selectTab = (k) => navigate(k === 'dati' ? schedaUrl(codeParam) : schedaUrl(codeParam, k), { replace: true });

    // Tutto il catalogo: serve per trovare la disciplina e, in creazione, per i doppioni
    const [catalog, setCatalog] = useState(null);
    const sport = useMemo(() => (isNew || !catalog ? null : catalog.find((s) => s.code === codeParam) ?? null), [catalog, codeParam, isNew]);

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [confirmShowed, setConfirmShowed] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [onContinue, setOnContinue] = useState(null);

    const [formLabel, setFormLabel] = useState('');

    const showError = (text) => {
        setModalTitle("Errore durante l'operazione");
        setModalText(text);
        setModalShowed(true);
    };

    const loadData = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        setLoadingShowed(true);
        const result = await new MyApiClient().getAdminSports(token);
        setLoadingShowed(false);

        if (!result || result.error) {
            showError(result?.error ?? 'Impossibile leggere il catalogo delle discipline');
            setCatalog([]);
            return;
        }
        setCatalog(result.map(mapSport));
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Il nome nel campo segue la disciplina letta (anche dopo un salvataggio)
    useEffect(() => {
        if (sport) setFormLabel(sport.label);
    }, [sport]);

    usePageTitle(isNew ? '' : sport?.label);
    // Stato accanto al titolo, cambiato e salvato subito (regola SUI 03/10/2026):
    // niente tasto Disattiva/Riattiva in fondo. Le discipline core non si disattivano.
    useStatoNelTitolo({
        visibile: !!sport,
        attivo: !!sport?.active,
        onCambia: async (attivo) => {
            if (sport.core) return { error: 'Le discipline core (con livelli, ruoli e traguardi dedicati) restano sempre attive.' };
            const token = getToken();
            if (!token) return { annullato: true };
            const result = await new MyApiClient().updateAdminSport(token, sport.code, { active: attivo });
            if (result && result.error) return { error: result.error };
            await afterWrite();
            return {};
        },
    });

    const afterWrite = async () => {
        await refreshSportsCatalog();
        await loadData();
    };

    const goBack = () => navigate(LIST_URL);

    const save = async () => {
        const label = formLabel.trim();
        if (label.length < 2) {
            showError('Il nome della disciplina deve avere almeno due caratteri.');
            return;
        }

        const token = getToken();
        if (!token) return;
        const apiClient = new MyApiClient();

        // Il codice resta quello: si cambia solo il nome
        if (label === sport?.label) return;
        setLoadingShowed(true);
        const result = await apiClient.updateAdminSport(token, codeParam, { label });
        setLoadingShowed(false);
        if (result && result.error) return showError(result.error);
        return afterWrite();
    };

    const remove = () => {
        if (!sport) return;
        setConfirmText(
            `Eliminare "${sport.label}"? L'operazione non si annulla. Se la disciplina e' gia' usata da qualcuno il sistema la rifiuta: in quel caso va disattivata.`,
        );
        setOnContinue(() => async () => {
            setConfirmShowed(false);
            const token = getToken();
            if (!token) return;
            setLoadingShowed(true);
            const result = await new MyApiClient().deleteAdminSport(token, sport.code);
            setLoadingShowed(false);
            if (result && result.error) return showError(result.error);
            await refreshSportsCatalog();
            navigate(LIST_URL);
        });
        setConfirmShowed(true);
    };

    // «Nuova disciplina» è una finestra della lista (si scrive solo il nome): il vecchio indirizzo la apre lì
    if (isNew) return <Navigate to={SPORT_CATALOG_CREATION_ENABLED ? `${LIST_URL}?nuova=1` : LIST_URL} replace />;

    const modals = (
        <>
            <Modal show={modalShowed} onHide={() => setModalShowed(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalText}</Modal.Body>
            </Modal>

            <Modal className="fade" show={confirmShowed} centered>
                <Modal.Header>
                    <Modal.Title>Sei sicuro?</Modal.Title>
                    <Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col className="col-xl-64">{confirmText}</Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>
                        Chiudi
                    </Button>
                    <Button className="btn btn-primary btn-add" onClick={() => { setConfirmShowed(false); if (onContinue) onContinue(); }}>
                        Continua
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>
        </>
    );

    // Codice che non corrisponde a nessuna disciplina
    if (!isNew && catalog && !sport) {
        return (
            <>
                {modals}
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        Nessuna disciplina con codice <strong>{codeParam}</strong>.
                    </Card.Body>
                </Card>
                <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                    <span />
                    <button type="button" className="btn btn-dark light" onClick={goBack}>Indietro</button>
                </div>
            </>
        );
    }

    const dati = (
        <>
            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Disciplina</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Nome</Form.Label>
                            <Form.Control type="text" value={formLabel} onChange={(e) => setFormLabel(e.target.value)} />
                        </Col>
                        {sport && (
                            <>
                                <Col md={6}>
                                    <Form.Label>Tipo</Form.Label>
                                    <div className="sid-readonly-value">{sport.tipo}</div>
                                    {sport.core && (
                                        <Form.Text className="text-muted">
                                            Ha livelli, ruoli e traguardi dedicati: non si disattiva e non si elimina.
                                        </Form.Text>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Traguardi</Form.Label>
                                    <div className="sid-readonly-value">
                                        {sport.traguardi === undefined
                                            ? '-'
                                            : `${sport.traguardi} attivi${sport.traguardiDisattivati > 0 ? `, ${sport.traguardiDisattivati} disattivati` : ''}`}
                                    </div>
                                </Col>
                            </>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                {sport && !sport.core ? (
                    <button type="button" className="btn btn-danger light" onClick={remove}>Elimina</button>
                ) : (
                    <span />
                )}
                <div className="d-flex gap-4">
                    <button type="button" className="btn btn-dark light" onClick={goBack}>Indietro</button>
                    {sport && (
                        <Button className="btn btn-primary" onClick={save}>Salva</Button>
                    )}
                </div>
            </div>
        </>
    );


    return (
        <>
            {modals}
            <div className="custom-tab-1">
                <Tab.Container activeKey={activeTab} onSelect={selectTab}>
                    <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                        <Nav.Item as="li" className="tabMenu">
                            <Nav.Link eventKey="dati">Dati</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li" className="tabMenu">
                            <Nav.Link eventKey="traguardi">Traguardi</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Tab.Content className="mt-4" mountOnEnter>
                        <Tab.Pane eventKey="dati">{dati}</Tab.Pane>
                        <Tab.Pane eventKey="traguardi">
                            <TraguardiTabella code={codeParam} onBack={goBack} />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </div>
        </>
    );
};

export default SchedaDisciplina;
